import type { ChannelPhotoJob } from "./channelActivity";
import { stockReviewIsDue } from "./stock";
import { isManualListingAccount } from "./manualListings";
import type { InventoryItem, MarketplaceAccount, MarketplaceListing } from "./types";

export function pendingPhotoImport(item: InventoryItem, jobs: ChannelPhotoJob[] = []) {
  return jobs.find((job) => job.workspaceID === item.workspaceID && job.itemID === item.id && ["queued", "saving"].includes(job.status));
}

export function inventoryAttention(item: InventoryItem, photoCount: number, listings: MarketplaceListing[], jobs: ChannelPhotoJob[] = []) {
  if (listings.some((listing) => listing.status === "failed")) return "Listing needs attention";
  if (listings.some((listing) => inventoryListingPresentation(listing).savedPrice)) return "Listing price differs";
  if (["sold", "archived"].includes(item.status)) return undefined;
  if (item.openingStockStatus === "review_required") return "Check opening stock";
  if (item.quantity === 0) return "Check stock";
  if (!photoCount && !pendingPhotoImport(item, jobs)) return "Needs photos";
  const run = item.research?.run;
  if (run && (item.research?.status !== "refined" || item.research.review?.conditionPending)) {
    if (run.status === "failed") return "Retry research";
    if (run.status === "needs_input") return "Research needs your input";
    if (run.status === "ready" && item.research?.review?.conditionPending) return "Check condition";
  }
  if (item.research?.status === "ready_to_refine" && item.research.provider.id !== "local_research_preview") return "Research ready";
  if (stockReviewIsDue(item)) return "Stock review due";
  return undefined;
}

export function inventoryListingSummary(listings: MarketplaceListing[]) {
  const states = listings.map((listing) => inventoryListingPresentation(listing));
  const live = states.filter((state) => state.live).length;
  const drafts = states.filter((state) => state.draft).length;
  const pending = listings.filter((listing, index) => listing.status === "publishing" && !states[index].live).length;
  return [live ? `${live} live` : "", drafts ? `${drafts} ${drafts === 1 ? "draft" : "drafts"}` : "", pending ? `${pending} publishing` : ""].filter(Boolean).join(" · ") || (listings.length ? "No active listings" : "Not listed");
}

export function inventoryListingPresentation(listing: MarketplaceListing, account?: MarketplaceAccount) {
  const observed = listing.observedChannel;
  const live = observed ? observed.status === "active" : listing.status === "live";
  const draft = ["draft", "ready"].includes(listing.status) && !observed;
  const changedPrice = live && observed?.price && (observed.price.amountMinor !== listing.price.amountMinor || observed.price.currency !== listing.price.currency);
  return {
    live,
    draft,
    price: live ? observed?.price ?? listing.price : listing.price,
    savedPrice: changedPrice ? listing.price : undefined,
    status: live ? "Live" : observed?.status === "ended" ? "Ended" : draft ? "Draft" : listing.status === "failed" ? "Failed" : listing.status === "publishing" ? "Publishing" : "Ended",
    note: listing.status === "failed" ? (live ? "Update failed" : "Couldn’t publish")
      : changedPrice ? "Price differs"
      : account && isManualListingAccount(account) ? "Managed manually"
      : listing.managementMode === "read_only" ? "Read only"
      : undefined,
    attention: listing.status === "failed" || Boolean(changedPrice),
    priceLabel: listing.status === "failed" && !observed?.price ? "Saved price" : draft ? "Draft price" : "Asking price"
  };
}
